// ─────────────────────────────────────────────────────────────
// Site content for peplaroofing.com. Edit text here, not in the pages.
// Images live in /public/images (see scripts/fetch-images.mjs).
// ─────────────────────────────────────────────────────────────

// Brand details.
//
// No address, license number, social profiles or reviews are set. Each of
// those is a switch, not a deletion: fill one in and the site starts showing
// it again (see the switches table in README.md). Until then the site makes
// no claim it can't back up.
export const brand = {
  name: "Pepla Roofing",
  legalName: "Pepla Roofing LLC",
  shortName: "Pepla",
  logo: "/images/logo.png",
  tagline: "Built to Rise. Built to Protect.",
  phone: "(602) 834-4707",
  phoneRaw: "+16028344707",
  email: "Contact@PeplaRoofing.com",
  // Lowercase form for mailto: links and email headers.
  emailRaw: "contact@peplaroofing.com",
  // No storefront. The site says which areas are served instead of an
  // address; set this to show one in the footer, Contact page and schema.
  address: "",
  addressCity: "Phoenix, Arizona",
  // Office hours are only shown when a walk-in address exists.
  hours: "",
  hoursLabel: "Office (walk-in)",
  // Years of ROOFING EXPERIENCE, not years Pepla Roofing LLC has existed.
  // The two are different numbers and the copy says "experience" for that
  // reason: the LLC's registration date is public, so a claim to be ten
  // years in business would be checkable and wrong.
  yearsExperience: "10+",
  // Arizona ROC license. Set both and the license line, schema and the
  // "licensed" language switch back on site-wide.
  rocNumber: "",
  license: "",
  // Google Business Profile — set the review URLs once the profile exists
  // and the reviews section and rating pill return.
  googleReviewUrl: "",
  googleWriteReviewUrl: "",
  // Social profiles. Each one appears in the footer only once it is set here.
  //
  // These are ALSO listed as schema.org "sameAs" in public/index.html, which
  // is where the RoofingContractor node lives — that block is static HTML and
  // cannot read this file, so adding a profile here means adding it there too.
  // Keep the two lists identical.
  //
  // Store the clean canonical URL — no ?fbclid=, ?is_from_webapp= or other
  // tracking parameters. They are per-click values that mean nothing to a
  // visitor, and in a sameAs they weaken the entity match.
  facebook: "https://www.facebook.com/profile.php?id=61595047769430",
  instagram: "https://www.instagram.com/peplaroofing",
  tiktok: "https://www.tiktok.com/@peplaroofing1",
  // Paste the public Yelp page (yelp.com/biz/...) to show it in the footer
  yelp: "",
  rating: "",
};

// Hero background video. Set enabled:false to go back to the still image.
//
// The clip is a 2.3-second seamless loop cut from the raven take-off footage.
// It is muted and has no audio track at all. Visitors who have asked for
// reduced motion, and those browsing with data saver on, get `heroImage`
// instead — see src/components/HeroMedia.jsx.
// Homepage hero background clip. Set `enabled: false` to fall back to the
// still photo everywhere. WebM is listed first because browsers that can play
// VP9 get the smaller file; Safari and iOS fall through to the MP4.
export const heroVideo = {
  // Off — the homepage hero is the still house photo (public/images/hero.jpg).
  // Set to true to bring the clip back; the files are still in public/video/.
  enabled: false,
  // Phones get the still photo instead. The clip is landscape, so a portrait
  // screen crops it to an unreadable center strip, and it isn't worth the
  // download on cellular. Set to 0 to play it on phones too.
  minWidth: 768,
  sources: [
    { src: "/video/hero.webm", type: "video/webm" },
    { src: "/video/hero.mp4", type: "video/mp4" },
  ],
  poster: "/images/hero-poster.jpg",
};

// Homepage hero photo. `heroImageBase` is the stem the responsive variants
// hang off — HeroMedia builds "<base>-768.webp", "-1200.jpg" and so on from
// it, so swapping the hero means regenerating those six files and changing
// this one line. `heroImage` is the plain-JPEG fallback for anything that
// understands neither <picture> nor srcset.
export const heroImageBase = "/images/hero-truck-trailer";
export const heroImage = `${heroImageBase}.jpg`;

// Wide hero photo reused by the Insurance Claims and Careers pages.
// Every image the site uses now ships in public/images — nothing is downloaded
// at build time, so the build works offline and can't break when a third-party
// host goes away.
export const afterRoof =
  "/images/about-story.jpg";

// Service area names (full details and pages: ./serviceAreas.js)
export { serviceAreas as serviceAreaList } from "./serviceAreas";

export const services = [
  {
    slug: "roofing",
    name: "Roofing",
    short: "Roof repair, replacement and inspections for tile, shingle, foam and flat roofs.",
    seoTitle: "Roofing Services in Phoenix, AZ | Pepla Roofing",
    seoDescription:
      "Roof repair, roof replacement, leak repair and free roof inspections for tile, shingle, foam and flat roofs across Phoenix and the Valley. Call (602) 834-4707.",
    hero: "/images/roofing-shingle-project.jpg",
    project: "/images/foam-roof-mountains.jpg",
    icon: "Home",
    intro:
      "Arizona's brutal sun, monsoon storms, and 100°+ heat demand a roof built to last. Our crews repair and replace tile, shingle, foam and flat roofs across the Valley — from a single leak after a monsoon to a full tear-off, and the tile underlayment work that sits in between.",
    features: [
      "Roof repair & roof leak repair",
      "Roof replacement & tear-offs",
      "Tile roof repair & underlayment replacement",
      "Asphalt shingle re-roofs",
      "Flat & foam roofing, recoats and repairs",
      "Monsoon, wind & storm damage repair",
      "Free roof inspections with photo reports",
      "Storm damage documentation for insurance",
    ],
    benefits: [
      { title: "Premium Materials", desc: "Quality tile, shingle and foam systems backed by manufacturer warranties." },
      { title: "Free Inspections", desc: "Honest, no-pressure roof assessments with detailed photo reports." },
      { title: "Repair Or Replace", desc: "We tell you which one your roof actually needs, and why, in writing." },
    ],
    related: { slug: "tile-roof-underlayment", eyebrow: "Tile roof leaking?", title: "Tile Underlayment Replacement", text: "On most Valley tile roofs the tile outlives the felt underneath it. Replacing the underlayment and resetting your existing tile is often the fix — not a whole new roof.", cta: "See Underlayment Replacement" },
    faqs: [
      {
        q: "How much does a new roof cost in Phoenix?",
        a: "It depends on the roof system, the size and pitch of the roof, the condition of the decking, and whether anything has to come off first — solar, HVAC, satellite mounts. Anyone who quotes a price before looking at the roof is guessing. We inspect for free and give you an itemized estimate with the manufacturer, line and color named.",
        link: { to: "/roof-replacement-cost", label: "What drives roof cost in Phoenix" },
      },
      {
        q: "Can my tile roof be repaired instead of replaced?",
        a: "Often, yes. On Arizona tile roofs the tile itself commonly outlasts the underlayment beneath it, which is the layer that actually keeps water out. Replacing underlayment in the affected areas and resetting the existing tile resolves a lot of leaks for a fraction of a full replacement.",
        link: { to: "/services/tile-roof-underlayment", label: "Tile underlayment replacement" },
      },
      {
        q: "My roof is leaking during monsoon season. What should I do first?",
        a: "Contain the water inside, photograph everything before you clean it up, and call us. We inspect seven days a week, 7:00 AM to 7:00 PM, and a team member calls to confirm the time. If the damage looks like a claim, our photo report is written so your carrier can work from it.",
      },
      {
        q: "How long does a roof last in Arizona?",
        a: "Concrete tile can last decades, but the underlayment under it usually gives out first — often somewhere around the two-decade mark, depending on sun exposure, ventilation and how it was installed. Shingles age faster here than in milder climates, and foam roofs depend on staying recoated. An inspection tells you where yours actually is.",
      },
      {
        q: "How often does a foam roof need to be recoated?",
        a: "Flat foam roofs live on their coating. Manufacturers generally expect a recoat around the five-year mark, and letting it go past that is what turns a maintenance job into a replacement, because the sun starts breaking down the foam itself.",
        link: { to: "/services/foam-roof-coating", label: "Foam roof coating & repair" },
      },
      {
        q: "Do you offer free roof inspections?",
        a: "Yes. Inspections and estimates are free, seven days a week from 7:00 AM to 7:00 PM. Those are daylight hours, so the earliest and latest appointments shift through the year with sunrise and sunset — we confirm the exact time when we call. You get photos of what we found and a written scope, whether or not you hire us.",
      },
      {
        q: "How do I check out a roofing contractor before I sign?",
        a: "Ask for the contractor's Arizona ROC license number and look it up yourself on the ROC website — it shows the license status and any complaints. Ask for proof of insurance too, and get the scope in writing with every product named by manufacturer, line and color.",
      },
    ],
  },
  {
    slug: "binsr-roof-inspections",
    name: "BINSR Roof Inspections",
    noProjectImage: true,   // no photo in the body of this page
    short: "Fast roof inspections, written scopes and repairs for Arizona home sales.",
    seoTitle: "BINSR Roof Inspections & Repairs | Phoenix, AZ",
    seoDescription:
      "Roof inspections, itemized repair scopes and repairs before close of escrow for Arizona home sales. BINSR-ready photo reports: (602) 834-4707.",
    hero: "/images/roof-inspection.jpg",
    icon: "FileCheck",
    intro:
      "A roof item does not kill a deal. Not knowing what it costs kills the deal. When an inspector writes \"roof at end of serviceable life\" with no number beside it, a buyer imagines forty thousand dollars and walks — when the real scope is often a fraction of that. We put a number on it fast, in writing, so the negotiation runs on facts instead of fear.",
    features: [
      "Written estimate within 24 hours of receiving the inspection report",
      "Roof inspections scheduled inside the inspection period",
      "Photo-documented report you can attach to the BINSR",
      "Itemized repair pricing, not a vague allowance",
      "Tile underlayment, flashing & penetration evaluation",
      "Repairs completed before close of escrow",
      "Billing through escrow available, so nobody pays up front",
      "Post-repair photos and paid invoice for the file",
    ],
    benefits: [
      { title: "Built For The Clock", desc: "The standard inspection period is 10 days. We schedule around that deadline, not ours." },
      { title: "Numbers Agents Can Use", desc: "A written scope with line-item pricing, so the BINSR asks for something specific." },
      { title: "Inspect And Repair", desc: "One contractor handles the inspection and the repair, with no second vendor to coordinate." },
    ],
    maintenance: {
      title: "Built Around The Agent, Not The Roof",
      intro:
        "Most of our transaction work comes from agents who have been burned by a contractor that showed up late, quoted a replacement on a roof that needed a repair, or went quiet three days before closing. Here is how we try not to be that.",
      cta: "Send Us An Inspection Report",
      benefits: [
        { icon: "Clock", title: "24-Hour Estimate", desc: "Email us the inspection report and we return a written, itemized estimate within one business day. No site visit needed to start." },
        { icon: "FileCheck", title: "BINSR-Ready Format", desc: "Photos, line items and prices laid out so they can be attached to the form directly rather than retyped." },
        { icon: "DollarSign", title: "Billed Through Escrow", desc: "Where the parties agree to it, we invoice escrow at close. Neither of your clients has to write a check mid-transaction." },
        { icon: "Handshake", title: "Second Opinions Welcome", desc: "If a listing agent thinks a buyer's roof bid is inflated, we will scope it independently and say so either way." },
      ],
      items: [
        { icon: "ShieldCheck", title: "We will tell you it's fine", desc: "If the roof does not need the work the inspector implied, that is what the report says. An honest 'no repair needed' saves more deals than a discount." },
        { icon: "Layers", title: "Repair before replace", desc: "On Valley tile the felt usually fails before the tile. Scoping underlayment on the affected slopes instead of a full tear-off is often the difference between a deal that closes and one that does not." },
        { icon: "RefreshCw", title: "One point of contact", desc: "The person who inspects the roof is the person who prices it and the person who schedules the repair. No handoffs to lose a day to." },
        { icon: "Wrench", title: "Scheduled around close", desc: "Once repairs are agreed, the work is booked against the closing date, and you get photos and a paid invoice for the file." },
        { icon: "Droplets", title: "Monsoon-season reality", desc: "July through September the phone does not stop. Transaction work is still scheduled first, because your deadline is contractual and a homeowner's is not." },
        { icon: "Award", title: "No pressure on your client", desc: "We do not upsell at the closing table. The scope on the estimate is the scope on the invoice." },
      ],
    },
    process: {
      eyebrow: "How It Works",
      title: "The Arizona 10 / 5 / 5 Timeline",
      intro:
        "Arizona resale deals written on the standard AAR contract follow the same rhythm. Knowing where the roof fits keeps a deal from stalling.",
      steps: [
        { step: "Days 1–10", title: "Inspection Period", desc: "The contract's default inspection period is 10 days (it can be negotiated). Everything, including the roof inspection, has to happen inside it. We aim to be on the roof within a couple of days of the call." },
        { step: "Day 10", title: "Buyer Delivers the BINSR", desc: "The buyer lists the items they want addressed. Our photo report and line-item pricing give the agent something concrete to attach instead of \"roof needs work.\"" },
        { step: "+5 Days", title: "Seller Responds", desc: "The seller has 5 days to agree to all, some, or none of the items. Sellers often call us for a second opinion or a competing scope at this point." },
        { step: "+5 Days", title: "Buyer Decides", desc: "The buyer then has 5 days to accept, negotiate, or cancel. Once repairs are agreed to, we schedule the work to finish before close of escrow and send photos and an invoice for the file." },
      ],
      disclaimer:
        "Timelines describe the standard AAR Residential Resale Real Estate Purchase Contract and can be changed by negotiation. This is general information, not legal or real estate advice — rely on the current AAR forms and your agent for a specific transaction.",
    },
    faqs: [
      {
        q: "What is a BINSR?",
        a: "The Buyer's Inspection Notice and Seller's Response is the Arizona Association of REALTORS form a buyer uses to tell a seller which inspection items they want addressed, and the seller uses to respond. Roof items are one of the most common things listed on it.",
      },
      {
        q: "How fast can you inspect a roof during the inspection period?",
        a: "Call us as soon as the general inspection flags the roof. The inspection period is short, so we prioritize transaction inspections and get you a written report with photos quickly.",
      },
      {
        q: "Do you provide a written scope and pricing for the BINSR?",
        a: "Yes. You get a photo report and itemized pricing for each item, so the BINSR can request specific repairs rather than a general allowance. That usually leads to a cleaner negotiation.",
      },
      {
        q: "Can repairs be completed before close of escrow?",
        a: "In most cases, yes. Once the repairs are agreed to, we schedule the work around the closing date and send photos and a paid invoice for the file.",
      },
      {
        q: "Do you work with realtors and property managers?",
        a: "Yes. We regularly work with agents, brokerages and community managers who need a roof looked at quickly during a transaction. Call (602) 834-4707 and we'll tell you honestly whether the roof needs a repair or a replacement.",
      },
      {
        q: "Does an older tile roof mean the sale needs a whole new roof?",
        a: "Often not. On Arizona tile roofs the tile frequently outlasts the underlayment beneath it, so replacing underlayment in the affected areas can resolve the issue at a fraction of a full replacement. We tell you which one your roof actually needs.",
      },
      {
        q: "Can you quote from the inspection report without going out first?",
        a: "Usually, yes, and it is the fastest way to start. Email us the roof pages of the inspection report and we will send a written estimate within one business day, with anything we cannot determine from photos clearly marked as needing a look. If the numbers matter to the negotiation we will get on the roof to confirm before anyone signs anything.",
      },
      {
        q: "Who pays, and when?",
        a: "Whoever the parties agree should pay. Where both sides agree to it, we can invoice escrow and get paid at close, so neither your buyer nor your seller is writing a check in the middle of a transaction. If escrow billing is not workable for the deal, we invoice on completion as normal.",
      },
      {
        q: "Will you give a second opinion on another roofer's bid?",
        a: "Yes, and we will tell you plainly if the other bid is reasonable. A listing agent looking at a $28,000 replacement quote on a roof that needs $6,000 of underlayment work deserves to know that — and so does an agent whose client got a number that is too good to be true.",
      },
      {
        q: "What if the roof genuinely does need replacing before close?",
        a: "Then we say so, with photos showing why. A full replacement inside an inspection period is tight but not impossible depending on material availability and the closing date. We will tell you honestly at the estimate stage whether the timeline is realistic, rather than taking the job and discovering it is not.",
      },
      {
        q: "Are you licensed, bonded and insured?",
        a: "Yes. Ask for the ROC number, the bond and the certificate of insurance and we will send all three before you put us in front of a client. Any contractor who hesitates at that request is telling you something.",
      },
    ],
  },
  {
    slug: "tile-roof-underlayment",
    name: "Tile Underlayment Replacement",
    hiddenFromNav: true,  // reachable from Roofing, BINSR, search and the sitemap
    short: "Replace the felt under your tile and reset the tile you already have.",
    seoTitle: "Tile Roof Underlayment Replacement | Phoenix, AZ",
    seoDescription:
      "Tile roof underlayment replacement across the Phoenix metro. We lift and stack your tile, replace the failed felt, and reset it. Free inspection: (602) 834-4707.",
    hero: "/images/underlayment-install.jpg",
    project: "/images/underlayment-battens.jpg",
    icon: "Layers",
    intro:
      "On most Arizona tile roofs, the tile is not the problem. The underlayment beneath it is — the layer that actually keeps water out, and the one that dries, cracks and fails long before the tile does. We lift and stack your existing tile, replace the underlayment, and set the tile back down.",
    features: [
      "Full tile underlayment replacement",
      "Section-by-section underlayment repair",
      "Tile lift, stack and reset",
      "Broken, slipped and cracked tile replacement",
      "Valley metal, flashing and penetration replacement",
      "Matching tile sourced for discontinued profiles",
      "Concrete, clay and lightweight tile",
    ],
    benefits: [
      { title: "Your Tile Stays", desc: "Sound tile gets reused, which is why this costs a fraction of a full tile roof replacement." },
      { title: "Scoped Honestly", desc: "If only two slopes are failing, we quote two slopes — and tell you what the rest has left." },
      { title: "Done To Spec", desc: "New underlayment, new flashing and new valley metal installed to the manufacturer's specification, not patched over." },
    ],
    related: { slug: "binsr-roof-inspections", eyebrow: "Buying or selling?", title: "BINSR Roof Inspections", text: "Underlayment is one of the most common roof items flagged during an Arizona home sale. We inspect, price the work line by line, and can complete it before close of escrow.", cta: "See BINSR Inspections" },
    climate: {
      eyebrow: "Why it fails here",
      title: "The Desert Ages Underlayment, Not Tile",
      // No `image` here on purpose — ServiceDetail drops to a single column and
      // runs the stat as its own band when a climate block has no photo.
      paragraphs: [
        "Tile is a rain screen. It sheds most of the water, takes the sun and the hail, and lasts fifty years or more doing it. The underlayment underneath is the part that actually waterproofs your house, and it is made of asphalt and fiber — material that heat destroys.",
        "Under tile on a July afternoon in the Valley, the deck can run far hotter than the air temperature outside. The asphalt in the felt slowly cooks out of it. Day to night, the roof expands and contracts through a swing that would be unusual anywhere else in the country, and the felt works against its fasteners every cycle. By the time the first monsoon pushes water sideways under the tile, the layer meant to stop it has gone brittle, shrunk back from the laps and split along the nail line.",
        "That is why a tile roof here almost never fails the way people expect. The tile looks fine from the street — because it is fine. The twenty-year-old paper under it is not.",
      ],
      bullets: [
        { label: "Ceiling stains after a driving rain, not a steady one", text: "Water gets under tile when wind drives it sideways. A leak that only shows up in a monsoon, and not in a calm all-day rain, points at the underlayment rather than a broken tile." },
        { label: "Stains that appear away from the obvious spot", text: "Water runs down the felt and along the deck before it finds a way through. The wet patch on your ceiling is often several feet from where it actually got in." },
        { label: "Granules collecting in the gutters or at downspouts", text: "That is the surface of the felt washing off. On a tile roof it means the layer underneath is wearing through." },
        { label: "Visible felt at the eave line that is cracked or curled", text: "Where the felt is exposed at the roof edge you can often see its condition without going up. Brittle, curled or shrunken edges indicate the rest." },
        { label: "A roof in its second decade with original paper", text: "If nobody has replaced the underlayment and the house is around twenty years old, the question is not whether it needs doing but how long you have." },
      ],
      stat: { value: "50+", label: "Years concrete tile can last — against roughly two decades for the underlayment beneath it" },
    },
    compare: {
      eyebrow: "What goes back down",
      title: "Not All Underlayment Is The Same",
      intro:
        "Once the tile is off, the material that goes down decides how long before anyone has to do this again. This is the part of the quote worth reading closely, because the labor to lift and reset your tile costs the same whichever one a contractor uses — and that labor is roughly 60% of the job. Paying for the cheapest material saves a little now and buys back the least time.",
      columns: ["Material", "Typical life in the Valley", "Relative material cost", "Best for"],
      rows: [
        {
          name: "30 lb asphalt felt",
          note: "The original paper on most Valley homes",
          cells: [
            "Around two decades, less on a hot west-facing slope with poor attic ventilation",
            "Lowest",
            "Matching an existing partial re-felt, or a roof you know you will replace outright before long",
          ],
        },
        {
          name: "Synthetic underlayment",
          note: "Woven polypropylene or polyethylene",
          cells: [
            "Longer than felt, and it does not dry out and shrink the same way",
            "Moderate",
            "Most full tile underlayment replacements — the usual best balance of cost and life",
          ],
        },
        {
          name: "Self-adhered (peel-and-stick)",
          note: "Modified bitumen membrane with an adhesive back",
          cells: [
            "Longest of the three, and it seals around every fastener that goes through it",
            "Highest",
            "Low-slope sections, valleys, eaves and anywhere wind-driven water concentrates",
          ],
        },
      ],
      footnote:
        "Most roofs do not need one material everywhere. A common and sensible scope is self-adhered in the valleys, at the eaves and on any low-slope section, with synthetic across the open field — which puts the money where water actually collects. Whatever we quote, the product is named by manufacturer and line on the estimate, so you can look it up before you sign.",
    },
    process: {
      eyebrow: "How It Works",
      title: "What The Job Actually Looks Like",
      intro:
        "Underlayment replacement is disruptive for a few days and invisible when it's done. Here's the sequence, so nothing about it is a surprise.",
      steps: [
        { step: "Step 1", title: "Inspection & Scope", desc: "We get on the roof, pull tile in a few places, and photograph what the felt underneath is actually doing. You get a written scope that says which slopes need work now and which don't." },
        { step: "Step 2", title: "Tile Lift & Stack", desc: "Tile comes off by hand and is stacked on the roof or the ground. Some breakage is normal on older tile, so the scope accounts for replacements up front rather than as a surprise line later." },
        { step: "Step 3", title: "New Underlayment", desc: "Old felt, fasteners and debris come off down to the deck. Damaged decking gets replaced, then new underlayment goes down with new valley metal, flashing and penetration boots." },
        { step: "Step 4", title: "Tile Reset & Cleanup", desc: "Your tile goes back down, broken pieces are swapped for matching replacements, and the site is magnet-swept. You get photos of the work under the tile, because you'll never see it again." },
      ],
    },
    faqs: [
      {
        q: "What is tile roof underlayment?",
        a: "It's the waterproof membrane between your tile and the roof deck. Tile sheds most of the water and takes the sun, but the underlayment is what actually keeps the house dry. When people say a tile roof is leaking, the underlayment is usually what failed.",
      },
      {
        q: "How long does underlayment last in Arizona?",
        a: "Original felt on Valley homes commonly starts failing somewhere around the two-decade mark. Heat, attic ventilation, roof pitch and the quality of the original install all move that number, which is why an inspection beats a rule of thumb.",
      },
      {
        q: "Do I need a whole new tile roof if it's leaking?",
        a: "Usually not. If the tile is in decent shape, replacing the underlayment and resetting the same tile fixes the leak and resets the clock on the roof. We'll tell you plainly when a roof is far enough gone that replacement is the better spend.",
      },
      {
        q: "How much does tile underlayment replacement cost?",
        a: "It comes down to square footage, pitch, how much tile breaks, how much decking needs replacing, and whether the whole roof or a couple of slopes need the work. We inspect for free and price it line by line rather than quoting a number over the phone.",
        link: { to: "/roof-replacement-cost", label: "What drives roof cost in Phoenix" },
      },
      {
        q: "Can you reuse my existing tile?",
        a: "In most cases, yes — that's the point of the job. Expect some breakage on older or brittle tile. We account for replacements in the scope and source matching tile where we can.",
      },
      {
        q: "What if my tile profile is discontinued?",
        a: "We source from tile yards and salvage inventory around the Valley, and it's often possible to find a match. When it isn't, we can pull tile from a less visible slope to keep the street-facing elevation uniform and blend the replacements where they show least.",
      },
      {
        q: "What if there are solar panels over the area?",
        a: "The panels have to come off before we can re-felt underneath them. We don't remove or reinstall arrays — your solar installer handles that — but we'll schedule our work around theirs so the roof isn't left open."
      },
      {
        q: "Should I do the whole roof or just the slope that's leaking?",
        a: "It depends on how far apart in age the slopes are. Underlayment on a south and west-facing slope ages faster than the north side, so if the failing slopes are a few years ahead of the rest, doing only those is a legitimate call and we'll scope it that way. But if everything went down at the same time and the rest is close behind, doing it in pieces means paying the tile lift and reset labor twice. We'll tell you which situation you're actually in and what the rest of the roof has left.",
      },
      {
        q: "How long does it take, and do I need to move out?",
        a: "A typical Valley tile roof runs a few days of crew time, longer if there's decking to replace or a lot of tile breakage. You can stay in the house the whole time. It is loud — tile coming off and going back on is not quiet work — and the attic may get dusty. We don't leave a roof open overnight; whatever is stripped in a day is dried in before the crew leaves.",
      },
      {
        q: "Why do contractors' square-foot prices vary so much?",
        a: "Often because they're not measuring the same thing. A lot of online pricing quotes dollars per square foot of your home's floor area, while the roof is quoted in roof area — which is typically 1.1 to 1.3 times larger on a low-slope home, and more on a steep or cut-up roof. A per-foot figure multiplied by the wrong number can be off by thousands. We measure the roof itself, in squares, and the estimate shows the measurement we used so you can compare quotes on the same basis.",
        link: { to: "/roof-replacement-cost", label: "What drives roof cost in Phoenix" },
      },
      {
        q: "Will I be able to tell the work was done properly?",
        a: "Not by looking at it — which is exactly the problem with this job. Once the tile is back down, every dollar you spent is invisible, and that's why underlayment is an easy place to cut corners. We photograph the deck once it's stripped, the new underlayment going down, and the flashing and valley metal before the tile covers them. Those photos go to you with the final invoice.",
      },
    ],
  },
  {
    slug: "foam-roof-coating",
    name: "Foam Roof Coating & Repair",
    hiddenFromNav: true,  // reachable from Roofing, the cost page, search and the sitemap
    short: "Recoats, repairs and replacement for the flat foam roofs the Valley is full of.",
    seoTitle: "Foam Roof Coating & Flat Roof Repair | Phoenix, AZ",
    seoDescription:
      "Foam roof recoating, blister and ponding repair, and flat roof replacement across Phoenix and the West Valley. Free inspection: (602) 834-4707.",
    hero: "/images/foam-roof-coated.jpg",
    project: "/images/foam-roof-finished.jpg",
    icon: "Droplets",
    intro:
      "Sprayed polyurethane foam covers a huge share of flat and low-slope roofs in the Valley, and it's the one roof system that genuinely rewards maintenance. The foam itself can last decades — but only while the coating over it holds. Let the coating go and the sun eats the foam underneath, and a recoat turns into a replacement.",
    features: [
      "Foam roof recoating (elastomeric)",
      "Blister, crack and pinhole repair",
      "Ponding water & drainage correction",
      "Parapet, scupper and flashing repair",
      "Flat roof leak repair",
      "New SPF foam roof installation",
      "Foam over existing flat roof systems",
      "Cool-roof reflective coatings",
    ],
    benefits: [
      { title: "Recoat Beats Replace", desc: "A roof caught before the foam is damaged gets recoated. One that isn't needs new foam — a different job at a different price." },
      { title: "Seamless By Design", desc: "Foam is sprayed as one continuous surface with no seams, which is why it suits parapets, penetrations and odd rooflines." },
      { title: "Cooler Inside", desc: "Foam insulates and the reflective topcoat sends sunlight back, which is the point on a flat roof in July." },
    ],
    related: { slug: "roofing", eyebrow: "Tile or shingle too?", title: "Roof Repair & Replacement", text: "Plenty of Valley homes are part flat, part pitched. We handle the whole roof — foam, tile and shingle — on one estimate rather than splitting it between trades.", cta: "See All Roofing" },
    process: {
      eyebrow: "How It Works",
      title: "Recoat, Repair Or Replace",
      intro:
        "Three different jobs get called \"foam roof work,\" and they cost very different amounts. The inspection decides which one you actually need.",
      steps: [
        { step: "Step 1", title: "Inspection", desc: "We check coating thickness and wear, probe for soft or saturated foam, and look at drains, scuppers and parapet flashing. Wet foam has to come out; dry foam under a worn coating just needs covering." },
        { step: "Step 2", title: "Prep & Repair", desc: "The roof is cleaned, blisters and pinholes are cut out and patched, damaged foam is removed and replaced, and low spots that hold water get built up before anything is coated." },
        { step: "Step 3", title: "Coating", desc: "Elastomeric coating goes on to the manufacturer's specified thickness, usually with granules embedded for walkability and UV protection. Thickness is what buys years, so the spec belongs on your estimate." },
        { step: "Step 4", title: "Documentation", desc: "You get photos of the prep and the finished surface, and a record of the coating and mil thickness used, so the next recoat can be scheduled instead of discovered." },
      ],
    },
    faqs: [
      {
        q: "How often does a foam roof need to be recoated?",
        a: "Manufacturers generally expect a recoat somewhere around the five-year mark, and the number depends on the coating used, the thickness applied, and how much sun and foot traffic the roof takes. A roof that gets recoated on schedule can keep going for decades on its original foam.",
      },
      {
        q: "What happens if I skip the recoat?",
        a: "The coating is the sunscreen. Once it wears through, UV starts breaking down the foam itself, and damaged foam has to be cut out and replaced rather than simply covered. That's the difference between a maintenance job and a roofing job.",
      },
      {
        q: "Can a foam roof be repaired, or does it all have to come off?",
        a: "Most foam roofs are repairable. Blisters, pinholes and storm punctures get cut out and patched, and saturated sections get replaced. Full replacement is for roofs where the foam is broadly wet or deteriorated.",
      },
      {
        q: "Why does water sit on my flat roof after a storm?",
        a: "Ponding usually means the roof lost its slope to drains or scuppers, or that a drain is blocked. Water standing for days accelerates coating failure, so the fix is to correct the drainage or build up the low area, not just coat over it.",
      },
      {
        q: "Does a foam roof lower cooling bills?",
        a: "Foam adds insulation the roof deck didn't have, and the reflective topcoat sends a large share of sunlight back rather than into the house. How much you notice depends on your attic, ductwork and existing insulation.",
      },
      {
        q: "Can foam be installed over my existing flat roof?",
        a: "Often, yes — foam is frequently sprayed over sound existing roofing, which avoids a full tear-off. Whether yours qualifies depends on what's under there and how much moisture it holds. That's what the inspection is for.",
      },
      {
        q: "How much does a foam roof recoat cost?",
        a: "It depends on square footage, how much prep and repair the surface needs first, and the coating and thickness specified. Recoating is far less than replacing foam, which is exactly why the timing matters.",
        link: { to: "/roof-replacement-cost", label: "What drives roof cost in Phoenix" },
      },
    ],
  },
];
export const values = [
  "Integrity and Precision",
  "Quality Craftsmanship",
  "Premium Products",
  "Expert Execution",
  "Elite Customer Care",
  "Enduring Value",
];

// "What goes on your roof": manufacturers whose products Pepla installs.
// Logo files live in public/images/brands/ (see the README there). Brands
// without a file show their name as text. Only list brands Pepla uses.
// ── Conversion content ──────────────────────────────────────────
// CONFIRM BEFORE LAUNCH: same-day availability, warranty
// lengths, and whether a lender partner is signed. Leave a value empty
// and the site simply doesn't make that claim.
export const inspection = {
  sameDay: true,            // false → the site says "fast scheduling" instead
  hours: "7 days a week, 7:00 AM – 7:00 PM",
  // A roof cannot be inspected safely or accurately in the dark, and Phoenix
  // daylight swings by roughly two and a half hours between June and December
  // — a 7:00 AM slot is before sunrise in winter and a 7:00 PM slot is after
  // sunset. `daylight` is the one place that caveat is written; everywhere the
  // site states the hours it should state this alongside them.
  daylight:
    "Start and end times shift with daylight — the earliest and latest " +
    "appointments move through the year with sunrise and sunset.",
  daylightEs:
    "Los horarios de inicio y cierre cambian con la luz del día — las citas " +
    "más temprano y más tarde se ajustan durante el año con la salida y la " +
    "puesta del sol.",
  note: "Inspections run 7 days a week, 7am–7pm. Start and end times shift with daylight, so the earliest and latest appointments move through the year with sunrise and sunset. Request a day and window online and a team member calls to confirm the exact time. Same-day appointments depend on crew routes.",
};

export const warranty = {
  // e.g. "50" once confirmed with the manufacturer program Pepla uses
  materialYears: "",
  // Workmanship term Pepla offers. Setting this turns the homepage
  // "Backed In Writing" card into "<n>-Year Workmanship Warranty"; empty and
  // the site simply does not make the claim. Must match the certificate
  // (Pepla-25-Year-Workmanship-Warranty.pdf) and Service Agreement 9.1.
  laborYears: "25",
};

// The Pepla Lifecycle Plan — the signature offering. Every other roofer in the
// Valley sells the same tile, shingle and foam; this is the one thing only
// Pepla sells, which is why it gets its own page rather than a line on a list.
//
// Pricing is live. The regulatory question that held it back — whether a
// long-dated prepaid promise to maintain a roof is a service contract or a
// regulated insurance product — largely went away when the 25-year prepaid
// tier did. An annual maintenance agreement, renewed yearly, is ordinary.
// Still worth an Arizona attorney confirming before volume, but it is no
// longer the open-ended commitment it was.
//
// Set showPricing to false to go back to "priced after the inspection".
export const lifecyclePlan = {
  name: "Pepla Lifecycle Plan",
  short: "Scheduled care that catches roof problems while they are still small.",
  showPricing: false,
  tagline: "A roof does not fail all at once. It fails from things nobody looked at.",
  intro:
    "Almost every expensive roof we see started as something cheap. A lifted tile, a cracked boot, a valley holding debris — none of them urgent, all of them quietly letting water into the deck for a season or two before anyone noticed the ceiling. The Lifecycle Plan exists because the difference between a $400 repair and a $14,000 replacement is usually just whether somebody was on the roof in time.",
  included: [
    { title: "Scheduled inspections", desc: "We come out on a set schedule rather than when something goes wrong, and you get the same photo report we give on a paid inspection." },
    { title: "Priority in storm season", desc: "After a monsoon the phone does not stop. Plan members are scheduled ahead of the queue, which is the week it matters most." },
    { title: "Minor repairs included", desc: "Resetting slipped tile, resealing a penetration, clearing a valley — the small things get handled during the visit instead of becoming a separate call-out." },
    { title: "A documented roof history", desc: "Every visit is photographed and kept. When you sell, that file answers the buyer's roof questions before they are asked — and an inspector's report is a lot less alarming next to it." },
  ],
  how: [
    { step: "1", title: "Start with an inspection", desc: "We get on the roof, document its real condition, and tell you what it needs now. Any work it already needs is quoted separately — enrolling does not hide an existing problem." },
    { step: "2", title: "Pick a level", desc: "One, two or three visits a year. Which one fits depends on the roof — its age, its pitch, how cut up it is, and how much shade and debris it sits under. That is a conversation we can only have after step one." },
    { step: "3", title: "We keep the schedule", desc: "You do not have to remember it. We book the visits, show up, send the photos, and tell you plainly when something needs more than the plan covers." },
  ],
  // Three tiers by SERVICE LEVEL, priced annually.
  //
  // The earlier five tiers were term-based (5/10/15/20/25 years) at $69-$189
  // a MONTH, which worked out at $828-$2,268 a year against a market of
  // $150-$600 — between 2.8x and 7.6x. They also charged more per month for a
  // longer commitment, which is backwards for a subscription.
  //
  // These prices are built from cost to serve at Pepla's own burdened labor
  // rate of $36.76/hr: one tech, an hour on the roof plus half an hour of
  // travel, consumables, a repair allowance and admin. Gross margin is 42%,
  // 42% and 32% — lower than the 77% assumed in the business plan, which was
  // modelled before the cost of the visits themselves was counted.
  //
  // Replace these with real numbers once ten plan visits have been completed
  // and the actual time on site is known.
  // Prices deliberately do NOT live here. showPricing:false only stops them
  // RENDERING — the strings would still ship inside the JS bundle and be
  // readable in page source, which is not "removed from the website".
  //
  // The published prices live in the Lifecycle Plan document
  // (pepla-docs/build/make_lifecycle_plan.py → Pepla-Lifecycle-Plan.pdf),
  // which is the single source of truth. To put pricing back on the site,
  // add `price` and `period` back here AND set showPricing:true — the tier
  // cards already guard on both.
  tiers: [
    {
      name: "Essential",
      term: "1 visit a year",
      best: "Newer roofs, or one you have just had re-felted",
    },
    {
      name: "Standard",
      term: "2 visits a year",
      best: "The common choice — before and after monsoon season",
    },
    {
      name: "Premium",
      term: "3 visits a year",
      best: "Larger, steeper or cut-up roofs with more to go wrong",
    },
  ],
  faqs: [
    {
      q: "Is this a warranty?",
      a: "No, and the difference matters. A warranty pays out when something fails. The Lifecycle Plan is maintenance — it is meant to stop the failure happening. Your manufacturer and workmanship warranties stay exactly as they are; this sits alongside them.",
    },
    {
      q: "What is not included?",
      a: "Full replacements, storm damage, structural repairs and anything that needs a permit are quoted separately. The plan covers routine care and the small fixes that come out of it. We will always tell you which side of that line a problem falls on, in writing, before any work starts.",
    },
    {
      q: "What happens if I sell the house?",
      a: "That is one of the better reasons to have it. The documented roof history transfers with the home and answers the questions a buyer's inspector will raise. Ask us about transferring the remaining term — the specifics depend on which plan you are on.",
    },
    {
      q: "Can I enroll a roof that already has problems?",
      a: "You can, but we will quote the existing work first and separately. A maintenance plan is not a way to roll a known repair into a monthly payment, and we would rather say that up front than have you find it out later.",
    },
  ],
};

// "Backed in writing" — the paperwork a customer actually ends up holding.
// Warranty card headings pick up real terms from `warranty` above once set.
export const paperwork = [
  { icon: "ClipboardCheck", title: "Inspection Report",
    text: "Photos of what we found on your roof, including the parts you can't see from the ground. Yours whether or not you hire us." },
  { icon: "FileText", title: "Scope Of Work",
    text: "Line-item pricing with every product named by manufacturer, line and color — never \"architectural shingle\" and a number." },
  { icon: "CloudLightning", title: "Insurance Claim Documentation",
    text: "Damage photographed and written up in a scope your carrier can work from, including after a denial." },
  { icon: "FileCheck2", title: "Manufacturer Material Warranty",
    text: "Because every product is named, you can read the manufacturer's own warranty before you sign. Coverage varies by system and installation." },
  { icon: "ShieldCheck", title: "Written Workmanship Warranty",
    text: "Our own labor warranty is written into your contract, with the term and what it covers spelled out before work starts — not promised on a handshake." },
  { icon: "Receipt", title: "Final Photos & Invoice",
    text: "Photos of the finished work, the materials actually used, and a paid invoice for your records and your home's file." },
];

export const financing = {
  // Paste Enhancify's application URL here and the Apply button appears on
  // /financing. Empty = the page invites a call instead.
  applyUrl: "",
  partnerName: "Enhancify",
  partnerLogo: "/images/enhancify.png",
  partnerNote:
    "Enhancify matches your application against multiple lenders at once, so one application can return several offers. Approval, rates and terms are the lender's decision, not ours.",
};

// Payment methods shown on /make-a-payment.
//
// NOTHING here collects a card number. Every paid method links out to a
// processor that is PCI-compliant and handles the card itself — peplaroofing.com
// never sees the number, which is the only safe way to run a payment page on a
// static site. Each method renders only when its URL or value is filled in, so
// an empty field means that row is simply absent rather than broken.
export const payments = {
  // The processor's hosted pay-an-invoice link. Stripe, Square, QuickBooks,
  // the RafterCore portal — whichever ends up being the system of record.
  // `processorName` is shown so a homeowner recognizes the name that will
  // appear on their statement instead of assuming the charge is fraudulent.
  onlineUrl: "",
  processorName: "",

  // RafterCore customer portal sign-in. The portal is already named on the
  // homepage as the place to view estimates and invoices.
  portalUrl: "",

  // Bank transfer / Zelle, if Pepla wants the handle public. A bare email or
  // phone is fine; anything that is not a public contact detail should stay off
  // a web page.
  zelle: "",

  // Checks. Needs brand.address, which is blank — the card hides itself until
  // there is an address to mail a check to.
  checkPayableTo: "Pepla Roofing LLC",

  // Methods that are always true, and need no configuration.
  acceptsPhonePayment: true,
  acceptsInPerson: true,
};

export const howItWorks = [
  { title: "Schedule Your Free Inspection", desc: "Call, text, or send the form. We'll set a time that works for you." },
  { title: "We Inspect and Photograph the Roof", desc: "A full look at the roof, flashing, penetrations and underlayment, with photos of anything we find." },
  { title: "You Get a Written Scope", desc: "Line-item pricing with every product named by manufacturer, line and color. No vague allowances." },
  { title: "We Do the Work", desc: "Experienced crews, daily cleanup, and an owner who checks in while the job runs." },
  { title: "Your Roof Is Backed", desc: "Warranty paperwork, final photos, and an invoice for your records." },
];

// Homepage project strip — Pepla's own work, shown in a simple grid.
//
// There is no social feed: Pepla has no Facebook or Instagram page set. If one
// is added later, set brand.facebook and turn socialFeed.plugin.enabled on to
// embed the page's posts, or set FB_PAGE_TOKEN for the photo feed. See
// README > Facebook feed.
// Empty — the homepage "See What We're Working On" strip is removed. The whole
// section disappears while this list is empty. To bring it back, add photos
// here; no other change is needed. The previous six are kept below, commented
// out, so they can be restored by uncommenting.
export const socialStrip = [
  // { src: "/images/about-tile-install.jpg", alt: "Concrete tile loaded on new underlayment" },
  // { src: "/images/roofing-tile-hero.jpg", alt: "Tile roof detail on an Arizona home" },
];

export const socialFeed = {
  enabled: false,          // true once a Facebook page token is configured
  endpoint: "/api/facebook-feed",
  count: 6,
  kicker: "Recent work",
  plugin: {
    enabled: false,        // true once brand.facebook is set
    pageUrl: "",
    height: 640,
  },
};

// Published cost ranges for /roof-replacement-cost.
//
// Built from Phoenix market per-square-foot rates applied to a ROOF area of
// 2,000-2,800 sq ft, which is the usual spread for a single-storey Valley
// home. Roof area, not floor area — most published roofing prices quote
// dollars per square foot of the *home*, and a roof runs about 1.1 to 1.3
// times that on a low slope and more on a steep or cut-up one. Quoting floor
// area is how an estimate ends up thousands under what the job costs, and
// saying so is half the point of this page.
//
// The ranges are deliberately wide. Narrow ranges on a page like this are
// either a guess or bait; what moves a job inside the range is explained
// above the table.
//
// Review these against real completed jobs each year and move `updated`.
export const roofCost = {
  showRanges: true,
  updated: "October 2026",
  ranges: [
    {
      system: "Tile underlayment replacement",
      note: "Your existing tile is lifted, stacked and reset. The usual fix on a leaking tile roof.",
      range: "$7,000 – $16,000",
    },
    {
      system: "Concrete tile re-roof",
      note: "Full tear-off, new tile and new underlayment.",
      range: "$14,000 – $30,000",
    },
    {
      system: "Clay tile re-roof",
      note: "Higher material cost and slower to set than concrete.",
      range: "$20,000 – $45,000",
    },
    {
      system: "Asphalt shingle re-roof",
      note: "Tear-off, new underlayment and architectural shingle.",
      range: "$8,000 – $17,000",
    },
    {
      system: "Foam roof recoat",
      note: "Flat sections. Recoat on schedule and you are replacing the coating, not the roof.",
      range: "$3,000 – $7,500",
    },
    {
      system: "Foam roof replacement",
      note: "When the coating was left too long and the sun has reached the foam.",
      range: "$9,000 – $20,000",
    },
    {
      system: "Repairs and leak work",
      note: "Most single-issue repairs land here. We quote after we have found the source.",
      range: "$450 – $2,500",
    },
  ],
  disclaimer:
    "Ranges are for a typical single-storey Maricopa County home with 2,000–2,800 square feet of roof area, and are not a quote. Pitch, height, how many slopes and valleys the roof has, the condition of the decking underneath, and whether solar, HVAC or satellite mounts have to come off all move a job inside — or outside — these numbers. Every Pepla estimate is written after an inspection, with the measurement we used shown on it.",
};

// Homepage promo strip. Set enabled:false to hide it site-wide.
export const promo = {
  enabled: false,
  headline: "Ask about our complimentary roof upgrade",
  text:
    "Every product on a Pepla estimate is named by manufacturer, line and color — including the upgrades we add to your roof at no charge. Which ones apply depends on the roof, so call and ask what yours qualifies for.",
  cta: "Call for details",
  note: "Mention this offer when you call.",
  // Shown beside the offer as a sample estimate. Name the actual upgrades in
  // `lines` once they're settled — the labels below are placeholders.
  quote: {
    title: "Roof replacement — sample scope",
    lines: [
      { label: "Upgrade #1", value: "Included" },
      { label: "Upgrade #2", value: "Included" },
      { label: "Upgrade #3", value: "Included" },
    ],
    upgradeLabel: "Your upgrade",
    upgradeValue: "$0",
    footnote: "Ask what qualifies on your roof.",
  },
};

// ── Analytics & call tracking ───────────────────────────────────
// Both stay OFF until an ID is added. Nothing loads, no cookies are set,
// and the privacy policy keeps saying so. Once ga4Id is set, visitors see a
// consent banner and GA4 loads only if they accept.
export const analytics = {
  ga4Id: "",              // e.g. "G-XXXXXXXXXX" from Google Analytics → Admin → Data streams
  callTrackingScript: "", // e.g. CallRail's swap.js URL, loaded only after consent
};

// Homepage "About Pepla" photo
export const aboutImage = "/images/about-tile-install.jpg";
// About page "Our Story" photo
export const aboutStoryImage = "/images/about-story.jpg";

// Insurance carriers whose claims Pepla has handled roofing work on.
// These are NOT partners, affiliates or approved vendors — Pepla is an
// independent licensed contractor and the page says so in print next to the
// logos. Each mark belongs to its owner and is shown only to identify the
// carrier. Keep this list to carriers Pepla has genuinely dealt with; a name
// here that nobody has worked a claim for is a claim the business cannot back.
export const insuranceCarriers = [
  { name: "State Farm", file: "state-farm" },
  { name: "Allstate", file: "allstate" },
  { name: "Farmers Insurance", file: "farmers" },
  { name: "USAA", file: "usaa" },
  { name: "Travelers", file: "travelers" },
  { name: "Liberty Mutual", file: "liberty-mutual" },
  { name: "American Family Insurance", file: "american-family" },
  { name: "Progressive", file: "progressive" },
  { name: "Badger Mutual Insurance", file: "badger-mutual" },
  { name: "Mercury Insurance", file: "mercury" },
  { name: "Nationwide", file: "nationwide" },
  { name: "GEICO", file: "geico" },
  // The logo is the AARP/Hartford co-branded lockup, which is the right one to
  // show: AARP does not underwrite anything. The Hartford writes the policy,
  // and Arizona is in their program — the states left out are CA, FL, NV, NY,
  // WA and DC.
  { name: "AARP Auto & Home Insurance from The Hartford", file: "aarp-hartford" },
  // A real Arizona carrier, part of the Western Mutual Insurance Group, writing
  // homeowners, condo, landlord and seasonal policies in this state.
  { name: "Arizona Home Insurance Company", file: "arizona-home" },
  // Amica writes homeowners here directly and keeps a Phoenix office.
  { name: "Amica Mutual Insurance", file: "amica" },
  // Erie writes in DC, IL, IN, KY, MD, NC, OH, PA, TN, VA, WI and WV — not
  // Arizona. The logo is in /images/brands/erie.png ready to go, but the
  // copy above this list says these are carriers whose claims we have worked
  // on for Valley homeowners, which cannot be true of an Erie policy here.
  // Uncomment if Erie enters Arizona or the claim changes.
  // { name: "Erie Insurance", file: "erie" },
];

// ── HOME REMODELING ──────────────────────────────────────────────────────
//
// GATED OFF until the B General Residential license is actually issued.
//
// A.R.S. § 32-1151 makes it unlawful to act as — or to OFFER to act as — a
// contractor in a classification you do not hold, and § 32-1124(B)(2) and
// § 32-1158 require the license number on advertising. Publishing these
// pages before the license is in hand is advertising work Pepla cannot
// lawfully contract for, and under § 32-1153 work performed outside the
// license cannot be sued on to collect.
//
// THE DAY THE B ISSUES: set `enabled: true` here and put the number in
// `brand.rocNumber` above. The page, the nav entries, the footer link and
// the sitemap all switch on from this one flag.
export const remodeling = {
  enabled: true,

  // Written workmanship term on remodeling work, in years. Separate from the
  // roofing warranty in `warranty` above — that one is 25 years and covers
  // roofs only. One year is the ordinary term for general remodeling.
  //
  // Note it does not shorten anything: under A.R.S. § 32-1162 a homeowner has
  // two years from the date work was last performed to file with the ROC,
  // whatever the contract says.
  warrantyYears: "1",

  // The B scope is "construct and repair all or any part of a residential
  // structure or appurtenance" — so this list stays residential, and the
  // categories lead with the ones adjacent to work Pepla already does.
  categories: [
    {
      icon: "Hammer",
      title: "Room Additions & Conversions",
      text: "Adding square footage means tying new structure into an existing roof, and that junction is where additions leak years later. It is the part most remodelers subcontract and the part we do ourselves.",
    },
    {
      icon: "SquareStack",
      title: "Patio Covers & Shade Structures",
      text: "Attached covers, ramadas and carports, built and flashed into the existing roofline rather than lagged onto the fascia and left to pull away.",
    },
    {
      icon: "PanelsTopLeft",
      title: "Windows & Exterior Doors",
      text: "Replacement in existing openings, flashed and sealed properly. Most window leaks in the Valley are flashing failures, not glass failures.",
    },
    {
      icon: "Paintbrush",
      title: "Stucco & Exterior Repair",
      text: "Cracked, spalled or water-stained stucco, parapet caps and the wall-to-roof transitions where Phoenix houses actually let water in.",
    },
    {
      icon: "ChefHat",
      title: "Kitchens",
      text: "Cabinets, counters, tile, flooring and finish carpentry, with licensed electrical and plumbing subcontractors named on your scope before work starts.",
    },
    {
      icon: "ShowerHead",
      title: "Bathrooms",
      text: "Tile, vanities, tubs and shower pans, with the waterproofing done properly beneath the tile rather than relying on grout to keep water out.",
    },
    {
      icon: "Grid2x2",
      title: "Flooring",
      text: "Tile, luxury vinyl plank and carpet, over a subfloor that has been checked and levelled first. Most flooring that fails in the Valley failed at the slab, not at the surface.",
    },
    {
      icon: "Ruler",
      title: "Finish Carpentry & Built-Ins",
      text: "Baseboards, casing, crown, closet systems, shelving and built-in cabinetry — the trim work that decides whether a remodel reads as finished or nearly finished.",
    },
    {
      icon: "Layers",
      title: "Drywall",
      text: "Hang, tape, float and texture, matched to the knockdown or orange peel already on your walls. Includes the ceiling repairs that follow a leak once the roof above them is fixed.",
    },
  ],

  // Stated plainly because the B classification requires it and because a
  // remodeler who tells you who is really doing the work is rarer than one
  // who does not.
  subcontracted: [
    "Electrical",
    "Plumbing",
    "Air conditioning & mechanical",
    "Boilers, pools, spas and water wells",
  ],
};

// ── PAINTING ─────────────────────────────────────────────────────────────
//
// Gated on the same licence as remodeling, deliberately: both are B General
// Residential work, so neither should ever be offered without the other being
// legal to offer.
//
// On the classification. A.A.C. R4-9-103 gives B the scope "construct and
// repair all or any part of a residential structure or appurtenance", and the
// only things it carves out are electrical, plumbing, air conditioning,
// boilers, pools, spas and water wells — painting is not among them. R-34
// Painting and Wall Covering exists for contractors who paint and nothing
// else; it is not a second licence a B holder needs on top. Worth confirming
// with the ROC before the first standalone paint-only job, since that is the
// edge of the reading rather than the middle of it.
export const painting = {
  get enabled() {
    return remodeling.enabled;
  },

  // Exterior paint on a Phoenix elevation is a shorter-lived product than
  // anything else Pepla sells, and saying so is better than being asked.
  warrantyYears: "2",

  exterior: [
    {
      icon: "SunMedium",
      title: "Stucco & Masonry Repaint",
      text: "The usual Valley job: chalked, faded, hairline-cracked stucco on a house that was last painted eight to twelve years ago. Crack repair and surface prep are most of the work and most of the cost.",
    },
    {
      icon: "PanelsTopLeft",
      title: "Fascia, Eaves & Trim",
      text: "The parts that fail first because they take the most sun and the most water. Done with the roof edge in mind, since that is where fascia paint and roofing meet.",
    },
    {
      icon: "DoorOpen",
      title: "Doors, Garage Doors & Iron",
      text: "Front doors, garage doors, security doors, gates and railings. Metal in this climate needs the rust dealt with first or the new coat lifts within a season.",
    },
    {
      icon: "Building2",
      title: "Block Walls & Parapets",
      text: "Perimeter walls, planters and parapet caps, including the efflorescence and moisture problems that make paint fail on block specifically.",
    },
  ],

  interior: [
    {
      icon: "Paintbrush",
      title: "Whole-Home & Single Rooms",
      text: "Walls, ceilings, baseboards and casing, with the furniture moved and masked properly rather than sheeted over and hoped for.",
    },
    {
      icon: "Layers",
      title: "Drywall Repair & Texture Matching",
      text: "Patches, cracks and water stains repaired and re-textured to match what is already there — knockdown, orange peel or smooth. A patch that does not match is worse than the hole was.",
    },
    {
      icon: "ChefHat",
      title: "Cabinet & Millwork Finishing",
      text: "Kitchen and bath cabinets degreased, de-glossed, sprayed and cured. The cheapest way to change a kitchen without replacing anything.",
    },
    {
      icon: "CloudLightning",
      title: "Post-Leak Restoration",
      text: "Stained ceilings and walls after a roof leak, sealed so the stain does not bleed back through. We fix the leak first, which is the part most painters cannot do.",
    },
  ],

  // Prep is the job. This list exists because it is what separates a repaint
  // that lasts a decade from one that chalks in three summers, and because a
  // homeowner comparing bids has no other way to see the difference.
  prep: [
    "Pressure wash the whole elevation and let it dry — paint over dust is paint that peels",
    "Grind and fill hairline and spider cracks in stucco; a moving crack is sealed, not painted over",
    "Cut out and patch spalled or delaminated stucco before any coating goes on",
    "Treat efflorescence and chalking on block and masonry rather than coating over it",
    "Sand, spot-prime and rust-treat metal down to sound material",
    "Caulk the window, door and penetration joints that are open",
    "Mask glass, hardware, roofing, pavers and plants — overspray on a tile roof is a callback",
  ],

  // Phoenix-specific, and the reason a summer repaint goes wrong.
  seasonNote:
    "Most coatings have a surface-temperature ceiling, and a west-facing Phoenix wall in July clears it by mid-morning. We work elevations on the shade rotation through the hot months and schedule full exteriors for the cooler half of the year, which is why an August quote may come with a start date in October.",
};

// ── ATTIC & ROOFLINE ─────────────────────────────────────────────────────
//
// The work that attaches to a roof visit rather than needing its own one.
//
// On classifications: ventilation and skylights sit inside roofing (R-42),
// which Pepla already holds. Insulation is R-40 and gutters and sheet metal
// are R-45 — neither is roofing, so both ride on the B General Residential
// scope. The whole page is gated on the same flag as remodeling so none of it
// can be advertised before that licence is in hand.
//
// On solar vents specifically: a self-contained solar attic fan carries its
// own panel and motor and never touches the premises wiring, so it installs
// as a roof penetration. A hardwired powered fan does touch it, and that
// connection goes to a licensed electrical subcontractor — R-11 is one of the
// trades B must subcontract.
export const roofline = {
  get enabled() {
    return remodeling.enabled;
  },

  // 2021 IECC, climate zone 2 — prescriptive ceiling value, with the cool-roof
  // alternative. Which code edition applies is the city's call, so the copy
  // says so rather than stating one number as universal.
  codeRValue: "R-49",
  coolRoofRValue: "R-30",

  insulation: [
    {
      icon: "Thermometer",
      title: "Blown-In Attic Insulation",
      text: "Fiberglass or cellulose blown to depth and depth-marked so anyone can verify it later. Most Valley homes built before the mid-2000s are sitting well under what current code asks for.",
    },
    {
      icon: "Wind",
      title: "Air Sealing First",
      text: "Top plates, can lights, bath fans and duct penetrations sealed before any insulation goes down. Insulation laid over an unsealed ceiling is a filter, not a barrier — the air moves straight through it.",
    },
    {
      icon: "PanelsTopLeft",
      title: "Soffit Baffles",
      text: "Baffles at every bay so the new insulation cannot bury the intake vents. Burying soffit vents is the most common way a well-meaning insulation job strangles a roof.",
    },
    {
      icon: "Ruler",
      title: "Depth Verification",
      text: "Before and after photos, the bag count, and the finished depth recorded in your file — the three things a home inspector or a buyer will ask for and that almost nobody can produce.",
    },
  ],

  ventilation: [
    {
      icon: "Sun",
      title: "Solar Attic Vents",
      text: "Self-contained solar-powered units — panel, motor and all — that move air without touching your electrical panel and without adding anything to your bill. The right answer on a roof with no usable ridge line.",
    },
    {
      icon: "AirVent",
      title: "Ridge, Off-Ridge & Turbine Vents",
      text: "Static exhaust sized to the attic rather than to whatever was already up there. Quiet, nothing to fail, and nothing to replace in ten years.",
    },
    {
      icon: "ArrowDownToLine",
      title: "Intake That Actually Works",
      text: "Exhaust without intake moves nothing. We count and clear the soffit vents, add them where there are none, and size the pair together.",
    },
    {
      icon: "FileCheck",
      title: "The Calculation, In Writing",
      text: "Net free area for your attic worked out and put in your file, because every manufacturer warranty on the Understanding Your Warranty page excludes damage caused by inadequate ventilation.",
    },
  ],

  // The thing most ventilation sales pitches leave out.
  ventWarning:
    "Adding a powered fan to a roof that already has continuous ridge venting can make things worse rather than better. A fan will take the easiest air it can find, and on a ridge-vented roof that is often the ridge itself rather than the soffits — so it short-circuits the system and pulls conditioned air out of the house instead. We will tell you which exhaust type your roof should have, and it is usually one type, not two.",

  gutters: [
    {
      icon: "Waves",
      title: "Seamless Gutters & Downspouts",
      text: "Rolled on site to the run so there are no seams to split, with downspouts routed to carry water away from the foundation instead of to the base of a wall.",
    },
    {
      icon: "Shield",
      title: "Drip Edge, Fascia & Valley Metal",
      text: "The metal that belongs under the roof edge and in the valleys, formed and set as part of the roof rather than added afterwards.",
    },
    {
      icon: "Hammer",
      title: "Custom Flashing & Sheet Metal",
      text: "Wall caps, pan flashing, chimney and skylight metal, cut and bent for your roof rather than pulled off a shelf in a size that nearly fits.",
    },
    {
      icon: "Droplets",
      title: "Splash & Drainage",
      text: "Splash blocks, extensions and discharge points placed so a monsoon cell does not wash the grade out from under a slab or stain the stucco it lands on.",
    },
  ],

  daylight: [
    {
      icon: "SunMedium",
      title: "Skylight Replacement",
      text: "The right time to replace a skylight is while the roof around it is open. New flashing kit, new curb if the old one is tired, integrated into the new system rather than sealed onto it.",
    },
    {
      icon: "Lightbulb",
      title: "Solar Tubes",
      text: "Daylight into a hallway, closet or interior bathroom with no exterior wall. A small roof penetration, flashed the same way a vent is.",
    },
    {
      icon: "AlertTriangle",
      title: "Yellowed & Crazed Domes",
      text: "Acrylic domes go cloudy and spider-cracked after fifteen or twenty Phoenix summers. If yours has, it is also close to leaking.",
    },
    {
      icon: "Wrench",
      title: "Leak Diagnosis",
      text: "Most skylight leaks are flashing failures, not glass failures. We find out which before anyone quotes you a new skylight.",
    },
  ],
};

// ── MANUFACTURER WARRANTIES ──────────────────────────────────────────────
//
// Plain-English breakdowns of the manufacturer warranties that come with the
// systems Pepla installs, each with the manufacturer's own document attached.
//
// Everything in here is taken from the attached PDF. Do not add a term that
// is not in the document — the whole value of this page is that a homeowner
// can click through and check it.
export const manufacturerWarranties = [
  {
    slug: "gaf",
    name: "GAF",
    title: "GAF Roofing System Limited Warranty",
    badge: "/images/brands/gaf-system-warranty-badge",   // .webp + .jpg
    badgeAlt: "GAF Roofing System Limited Warranty seal",
    pdf: "/docs/gaf-roofing-system-limited-warranty.pdf",
    pdfSize: "2.4 MB",
    effective: "Applies to GAF products installed after January 1, 2025.",
    lede:
      "GAF's system warranty covers manufacturing defects in the shingles and the GAF accessories around them. It is not a warranty on the installation — that is ours — and it is not storm insurance. Knowing which of the three is which is most of what a homeowner needs.",

    // The headline numbers, for LayerLock-labelled Timberline, which is what
    // this system warranty is written around.
    terms: [
      { label: "Shingle defects", value: "Lifetime", note: "Non-prorated for the first 10 years (GAF calls that the Smart Choice Protection Period). After that the payout is reduced for the use you have had." },
      { label: "Labor", value: "10 years", note: "Full reasonable labor cost during the Smart Choice period only. After year 10 GAF covers materials, not labor." },
      { label: "Wind", value: "15 years", note: "No maximum wind speed under WindProven, if you qualify. Otherwise 130 mph with the special nailing pattern, 110 mph without." },
      { label: "Algae", value: "25–30 years", note: "StainGuard Plus is 25 years, StainGuard Plus PRO is 30. Blue-green algae only." },
    ],

    covers: [
      "Manufacturing defects in GAF shingles that cause leaks",
      "Manufacturing defects in GAF accessories — ridge cap, starter strip, leak barrier, roof deck protection, Cobra attic ventilation",
      "Shingles failing to seal, blowing off or suffering wind damage because of a manufacturing defect",
      "Pronounced blue-green algae discoloration on StainGuard-labeled shingles",
      "Full reasonable labor to repair or re-cover defective product — during the first 10 years only",
    ],

    excludes: [
      "Hail, fire, and wind above the rated speed — those are insurance claims, not warranty claims",
      "Inadequate ventilation, and damage caused by it",
      "Improper installation, where the installation caused the damage",
      "Foot traffic, impact, and anything blown onto the roof",
      "Tear-off and disposal costs — never covered, even in year one",
      "Underlayment that is not GAF, metal work and flashings",
      "Damage to the inside of the building, including mold",
      "Low-slope membranes and Master Flow ventilation — those carry their own separate warranties",
    ],

    // The eligibility rules are the part most contractors never mention, and
    // the part that decides whether the warranty a homeowner thinks they have
    // actually exists.
    qualify: [
      "GAF shingles carrying a Lifetime term, plus at least three qualifying GAF accessories",
      "For WindProven: LayerLock-labeled shingles plus four accessories — ridge cap, starter strip, roof deck protection, and either a leak barrier or attic ventilation",
      "The nailing pattern GAF specifies — four nails per shingle on LayerLock, six on other GAF shingles — with GAF starter strip at both eaves and rakes",
      "Proof of purchase showing the installation date, kept by you",
    ],

    transfer:
      "Once, to one subsequent owner, who has to notify GAF in writing within a year of the sale. Transfer inside the first 10 years and the new owner gets the same coverage; transfer after and it drops to a two-year term.",

    // GAF publishes a tier comparison. Worth linking because the document we
    // attach is ONE tier of several, and a homeowner reading this page could
    // reasonably assume it is the only GAF warranty there is.
    sideNote: {
      heading: "This is one tier. GAF has several",
      body:
        "The Roofing System Limited Warranty above sits in the middle of GAF's residential range. Below it is a basic Shingle & Accessory warranty; above it are System Plus, Silver Pledge, Golden Pledge and President's Club, which add things this one excludes — tear-off, disposal and workmanship coverage among them. Which tier a roof qualifies for depends on the products installed and, on the upper tiers, on the contractor holding GAF certification. Ask us which tier yours qualifies for before you sign, not after.",
      linkLabel: "Compare GAF's residential warranties",
      linkUrl: "https://www.gaf.com/en-us/resources/warranties/residential",
      linkNote: "GAF's own comparison page",
    },

    claim: {
      window: "30 days from noticing the problem",
      note: "Notice to your contractor is not notice to GAF — that is stated in the document. Do not repair or replace before GAF has evaluated the claim, or it can be denied.",
      routes: [
        "gaf.com/contact",
        "1-800-458-1860",
        "warrantyclaims@gaf.com",
      ],
    },

    // What actually bears on a roof in Maricopa County.
    local: [
      { h: "Ventilation is an exclusion, and this is Phoenix", p: "GAF will not cover damage caused by inadequate ventilation. On a Valley roof running 150°F-plus deck temperatures through the afternoon, that is not a footnote. We size ventilation to code on every job and put the calculation in your file." },
      { h: "Algae coverage is worth very little here", p: "StainGuard covers blue-green algae, which needs humidity to grow. It is a real benefit in Florida. In Phoenix it is close to irrelevant, and it is not a reason to choose one shingle over another." },
      { h: "Monsoon damage is usually an insurance claim", p: "Wind above the rated speed and hail are both excluded. A microburst that strips shingles is a claim against your homeowner's policy, not against GAF." },
      { h: "Labor stops at year ten. Ours does not", p: "GAF pays labor for the first ten years and materials after that. Our own workmanship warranty runs the full term — so from year eleven, if the problem is how the roof was installed, you are still covered by us." },
    ],
  },

  {
    slug: "owens-corning",
    name: "Owens Corning",
    title: "Owens Corning Standard Product Limited Lifetime Warranty",
    badge: "/images/brands/oc-limited-lifetime-badge",   // .webp + .jpg
    badgeAlt: "Owens Corning Limited Lifetime Warranty seal",
    pdf: "/docs/owens-corning-standard-product-limited-warranty.pdf",
    pdfSize: "454 KB",
    effective: "Applies to Owens Corning shingles purchased after August 1, 2026.",
    lede:
      "Owens Corning's standard warranty works the same way GAF's does — it covers manufacturing defects in their own products, not the installation and not storm damage. Two things set it apart: the components rule that decides whether your accessories are covered at all, and a non-prorated window Owens Corning calls TRU PROtection.",

    // Headline numbers for the Duration Series, which is the line the Limited
    // Lifetime seal is usually attached to. Supreme is a 25-year shingle with
    // different numbers — see the table at the end of the PDF.
    terms: [
      { label: "Shingle defects", value: "Limited Lifetime", note: "Lifetime only on a single-family detached home the owner lives in. Any other structure — a rental, a condo, an HOA building, a church, a school — is a 40-year term with a 5-year TRU PROtection period." },
      { label: "TRU PROtection", value: "10 years", note: "The non-prorated window. Inside it Owens Corning pays for the replacement product plus the labor directly required. After it, payment is materials only, prorated — a year-15 claim on a Limited Lifetime shingle pays 72 percent of the original product cost." },
      { label: "Wind", value: "130 mph", note: "15-year wind period. Duration Series rises to 160 mph with the Enhanced Wind application, which has its own component and fastening requirements. Oakridge is 110 mph on a 4-nail application and 130 with 6 nails plus starter at eaves and rakes." },
      { label: "Algae", value: "25 years", note: "Only on shingles labeled Algae Resistant, and only with a qualifying Owens Corning hip and ridge. Without one it drops to 10 years with a 1-year non-prorated period." },
    ],

    covers: [
      "Manufacturing defects in Owens Corning shingles",
      "Owens Corning hip and ridge shingles, which always match the shingle term",
      "Other Owens Corning components — starter, underlayment, ice and water barrier, VentSure ventilation — but only if three of the five component categories are installed",
      "Blow-offs and wind damage after the shingles have thermally sealed, up to the rated wind speed",
      "Brown-black staining from Gloeocapsa magma on Algae Resistant shingles",
      "Labor directly required to repair, replace or re-cover defective product — during the TRU PROtection period only",
    ],

    excludes: [
      "Hail, strong storms and wind above the rated speed — those are insurance claims",
      "Blow-offs before the shingles have thermally sealed, which in cool weather can take until the season turns",
      "Removal and disposal of the old roof, and removal or replacement of solar panels and rooftop equipment",
      "Non-Owens Corning products — flashing, fasteners, pipe boots, metal work, wood decking",
      "Improper or inadequate ventilation, unvented attics, and any damage caused by them",
      "Foot traffic, falling branches, and damage from power washing, painting or coatings applied later",
      "Pre-existing conditions and failures in the structure beneath — cracked chimney mortar, faulty counterflashing, bad gutters",
      "VentSure off-ridge exhaust vents, which are named as excluded",
      "Any cost Owens Corning did not authorize in advance",
    ],

    // The components rule is the whole ball game on this warranty and almost
    // nobody explains it at the kitchen table. Three of five, or the
    // accessories fall back to their own much shorter individual warranties.
    qualify: [
      "Owens Corning shingles, bought new by the homeowner — not the contractor — for a property in the US or Canada",
      "Three or more of these five component categories, or the components are not covered under this warranty at all: hip and ridge; VentSure intake or exhaust ventilation; Owens Corning, RhinoRoof U20 or Titanium synthetic underlayment; Owens Corning starter shingle; WeatherLock, Titanium or RhinoRoof self-adhered ice and water barrier",
      "A self-adhered ice and water barrier over the entire deck counts for two of the five on its own",
      "Hip and ridge is the exception — DecoRidge, DuraRidge, ImpactRidge, ProEdge and RIZERidge match the shingle term automatically",
      "For the 160 mph Enhanced Wind coverage: Duration Series shingles, Owens Corning starter at eaves and rakes, qualifying hip and ridge, qualifying underlayment, and either an ice and water barrier or VentSure ventilation — installed strictly to the Enhanced Wind instructions",
      "Proof of purchase showing the installation date, kept by you",
    ],

    transfer:
      "Once, to one subsequent owner, who has to contact Owens Corning within 60 days of the sale with proof of purchase and the installation date. Transfer inside the TRU PROtection period and the new owner gets the balance of coverage; transfer after it and the defect coverage drops to two years from the sale. Wind and algae coverage transfer in full either way.",

    sideNote: {
      heading: "Register the warranty, and keep the paperwork",
      body:
        "Owens Corning runs registration and transfers through their own site. Registering does not create coverage you would not otherwise have, but it puts your installation date on record with the manufacturer, which is the thing a homeowner is least likely to be able to produce fifteen years later when it matters. We register your roof for you and give you the confirmation. If you ever sell the house, the 60-day transfer window starts at closing, and the second link is where the new owner does it.",
      links: [
        {
          label: "Register a standard warranty",
          url: "https://www.owenscorning.com/en-us/roofing/warranty/register-standard",
          note: "Owens Corning's registration form",
        },
        {
          label: "Manage or transfer a warranty",
          url: "https://www.owenscorning.com/en-us/roofing/warranty#manage-your-warranty",
          note: "Owens Corning's warranty hub",
        },
      ],
    },

    claim: {
      window: "30 days from noticing the problem",
      note: "Do not repair or replace before Owens Corning has made a determination — the document says the claim may be denied if you do. They may ask you to pay for photographs or shingle samples, and they have the right to inspect the roof. Nothing is reimbursed unless it was approved in advance.",
      routes: [
        "owenscorning.com/roofing — Manage Your Warranty",
        "Owens Corning Roofing and Asphalt, LLC, One Owens Corning Parkway, Toledo, OH 43659",
      ],
    },

    local: [
      { h: "Thermal sealing is not usually our problem here", p: "Shingles have to bake in direct sun to seal, and until they do, wind damage is not covered. In most of the country that is a real winter risk. In the Valley a roof installed in any month seals quickly — one of the few places the climate works in your favor on a warranty." },
      { h: "Ventilation is an exclusion, and the sun here is not", p: "Owens Corning will not cover damage from improper or inadequate ventilation, unvented attics or enclosed rafter assemblies. On a Phoenix roof that is the single most likely way to void coverage without knowing it. We size ventilation to code and keep the calculation in your file." },
      { h: "Algae coverage buys you almost nothing in Phoenix", p: "The 25-year algae warranty covers one organism that needs humidity to grow. It is a genuine benefit on the Gulf Coast. Here it is close to irrelevant, and the hip and ridge requirement behind it is not a reason to pick a shingle." },
      { h: "Three components, or your accessories are not covered", p: "This is the part worth reading twice. Install Owens Corning shingles with two accessories and the accessories fall back to their own short individual warranties. Our scopes name every component by manufacturer and line, so you can count them yourself before you sign." },
      { h: "Monsoon wind above the rating is an insurance claim", p: "130 mph standard, 160 with the Enhanced Wind application. Above that — and hail at any speed — is a claim against your homeowner's policy, not against Owens Corning. The National Weather Service record for your county is what settles it." },
      { h: "After year ten it is materials only. Ours is not", p: "Once TRU PROtection ends, Owens Corning pays a prorated share of the product and no labor. Our own workmanship warranty runs its full term, so if the problem is how the roof was installed, you are still covered by us long after the manufacturer stops paying anyone to climb up there." },
    ],
  },

  // TAMKO's entry is the self-adhering membrane, not their shingles. Those are
  // two different documents with very different terms, and a homeowner with
  // TAMKO shingles who read this section and assumed it applied to them would
  // be badly misled — hence the title and the first line of the lede.
  {
    slug: "tamko",
    name: "TAMKO",
    title: "TAMKO SA Roofing System Limited Warranty",
    // No warranty seal exists for this one, so the card carries the
    // manufacturer's mark instead. The link still opens the document.
    logo: "/images/brands/tamko.png",
    logoAlt: "TAMKO",
    pdf: "/docs/tamko-sa-roofing-system-limited-warranty.pdf",
    pdfSize: "97 KB",
    effective: "Applies to TAMKO SA products sold on or after June 1, 2017. Covers the United States except Hawaii and Alaska.",
    lede:
      "This one is for flat roofs, not shingles. TAMKO's self-adhering Base and Cap sheets are a low-slope membrane system, and plenty of Valley homes have one over a patio, an addition or the whole house. It is a shorter and much narrower warranty than the shingle warranties above, and it is worth knowing that before you assume a flat roof is covered the same way a pitched one is.",

    terms: [
      { label: "Term", value: "15 years", note: "180 months, running from the date the material was bought — not from the day the roof was finished." },
      { label: "Non-prorated", value: "First year", note: "One year. Inside it, TAMKO replaces the defective material or pays the dollar value per square with no reduction." },
      { label: "After year one", value: "Prorated", note: "Months left divided by 180. TAMKO's own example: 60 months remaining pays one third of the material. The rest is yours." },
      { label: "Labor", value: "None", note: "Material only, for the whole 15 years. TAMKO does not pay to remove the old membrane, install the new one, or dispose of anything." },
    ],

    covers: [
      "Manufacturing defects in TAMKO SA Base and Cap sheets that directly cause leaks",
      "Replacement material, as a certificate redeemable at a TAMKO distributor, or the cash value per square at TAMKO's option",
      "Full value of the defective material during the first year, with no proration",
    ],

    excludes: [
      "Standing water — ponding on a flat roof is specifically excluded",
      "Inadequate deck slope or drainage, and any leak that comes from it",
      "Faulty installation, and anything not applied strictly to TAMKO's published instructions",
      "Acts of God — wind, hail, storm, lightning, ice — and impact from objects",
      "Damage from rooftop equipment, named in the document: air conditioners, heating units, antennas, solar",
      "Foot traffic on the roof",
      "Tear-off, removal and disposal of any material, including in year one",
      "Flashings and metal work that are not TAMKO SA products",
      "Damage to the building inside or out, and to anything in it",
      "Chemicals, solvents, paints and salt exposure, and any membrane painted or altered after it left the factory",
      "Moss, algae, mildew and other growth, and debris from overhanging trees",
      "Repairs attempted by anyone other than TAMKO or its authorized representative",
    ],

    qualify: [
      "TAMKO SA Base and Cap sheets installed together as a system, strictly to TAMKO's published application instructions",
      "Used as a normal roof application — anything else is sold as is, with no warranty of any kind",
      "Documentary proof of purchase, which you have to produce when you file",
      "The warranty form filled in and kept — owner name, address, application date, contractor and where the material was bought",
    ],

    transfer:
      "It does not transfer. The document is explicit: this warranty is personal to the original owner and is not assignable or transferable under any circumstances. Selling the building ends it. That is a real difference from the shingle warranties above, both of which transfer once.",

    sideNote: {
      heading: "TAMKO shingles are a different document",
      body:
        "This warranty covers the SA membrane system only. TAMKO's asphalt shingles — Heritage, Titan XT, Elite Glass-Seal, StormFighter FLEX — carry their own separate Fiberglass-Asphalt Shingle Limited Warranty with entirely different terms, including a Full Start period and enhanced tiers that require a certified contractor. TAMKO also publishes a separate self-adhering single-ply membrane warranty written specifically for Arizona markets. If we spec a TAMKO product on your roof, the document that governs it is the one that comes with that product, and we will put a copy in your file. Ask us which one applies before you sign.",
      links: [
        {
          label: "TAMKO's warranty center",
          url: "https://www.tamko.com/warranty-center",
          note: "Overview of TAMKO's warranties",
        },
        {
          label: "All TAMKO warranty documents",
          url: "https://www.tamko.com/professionals/download-gallery/documents?#limitedwarranties",
          note: "TAMKO's own download gallery",
        },
      ],
    },

    claim: {
      window: "30 days from discovering the problem",
      note: "Notice to your contractor, dealer or builder is not notice to TAMKO — the document says so directly. Send proof of purchase with the notice. TAMKO can require a questionnaire, photographs and material samples at your expense, and can inspect the roof. Repair or replace before they have resolved the claim and it may be denied.",
      routes: [
        "1-800-441-7190",
        "Certified mail: TAMKO Building Products, P.O. Box 1404, Joplin, MO 64802-1404",
      ],
    },

    local: [
      { h: "Ponding water is excluded, and this is a monsoon town", p: "Standing water is named in the exclusions, and so is inadequate slope or drainage. After a July storm, water that sits on a flat roof is the single most common low-slope complaint in the Valley — and it is not a TAMKO claim. Slope, drains and scuppers are a design and installation question, which is to say ours." },
      { h: "Your A/C sits on that roof", p: "Damage caused by installing, maintaining or using rooftop equipment is excluded by name, air conditioners included. On a Phoenix flat roof that is not hypothetical — it is the most-trafficked square footage up there. Walk pads and proper curbing at the unit are worth insisting on, and they are a workmanship item, not a warranty one." },
      { h: "This one does not follow the house", p: "The shingle warranties above transfer once to the next owner. This one ends at the sale, full stop. If you are putting a flat roof on a rental or a house you expect to sell, that matters, and it is a reason to pay attention to who stands behind the installation." },
      { h: "Most of the value is in year one", p: "After twelve months it is a prorated material credit and nothing else — no labor, no tear-off, no disposal. A valid claim in year eight pays for roughly half the membrane and none of the work. Our workmanship warranty is what is actually standing behind that roof for the rest of its life." },
    ],
  },
];

export const productBrands = [
  { name: "Owens Corning", file: "owens-corning" },
  { name: "GAF", file: "gaf" },
  { name: "CertainTeed", file: "certainteed" },
  { name: "TAMKO", file: "tamko" },
  { name: "IKO", file: "iko" },
  { name: "Atlas Roofing", file: "atlas" },
  { name: "Eagle Roofing Products", file: "eagle" },
  { name: "Westlake Royal Building Products", file: "westlake" },
  { name: "Brava Roof Tile", file: "brava" },
  { name: "Gibraltar Building Products", file: "gibraltar" },
  { name: "DuPont Tyvek", file: "tyvek" },
  // Foam and elastomeric coating manufacturers. Grouped after the shingle and
  // tile brands so the strip reads in the same order as the services list.
  { name: "Gaco", file: "gaco" },
  { name: "Henry", file: "henry" },
  { name: "APOC", file: "apoc" },
  { name: "General Coatings", file: "general-coatings" },
  { name: "Everest Systems", file: "everest" },
];

// Trust bar. The star rating lives in the hero, so keep these different —
// concrete facts beat repeating the same number twice on one screen.
export const stats = [
  { value: "28", label: "Maricopa County Cities" },
  { value: "7 Days", label: "A Week For Inspections" },
  { value: "Complimentary", label: "Inspections & Estimates" },
];

// Customer reviews. Empty until Pepla has its own — the homepage hides the
// whole section rather than showing placeholders, and no review schema is
// emitted. Add entries in this shape once real reviews exist:
//   { name: "First", location: "Verified on Google", service: "Roof Repair",
//     rating: 5, text: "…" }
export const testimonials = [];

export const faqs = [
  {
    q: "What areas do you serve?",
    a: "We serve cities and towns across Maricopa County, including Phoenix, Glendale, Peoria, Surprise, Sun City, Goodyear, Buckeye, Scottsdale, Mesa, Chandler, Gilbert and more. See our Service Areas page for the full list.",
  },
  {
    q: "What roofs do you work on?",
    a: "Concrete and clay tile, asphalt shingles, flat roofs and foam — the four systems Valley homes actually have. We repair and replace all of them, and we'll tell you honestly which one your roof needs.",
  },
  {
    q: "Do you offer free estimates?",
    a: "Absolutely. We provide free, no-obligation estimates and honest inspections with detailed photo reports so you know exactly what you're getting.",
  },
  {
    q: "Can you help if my roof has storm damage?",
    a: "Yes. We'll inspect the roof, document the damage with photos and a written report you can share with your insurance company, and handle the repair once your claim is approved.",
  },
  {
    q: "How long does a typical roof replacement take?",
    a: "Most residential roof replacements are completed in 2–4 days depending on size, material, and weather. We'll give you a clear timeline before we start.",
  },
  {
    q: "What types of roofing do you install?",
    a: "We install and repair concrete and clay tile, asphalt shingles, flat roofing, and foam roofing systems using premium, warranty-backed materials.",
  },
];

export const whyChoose = [
  { icon: "FileCheck", title: "Everything In Writing", desc: "A written scope with every product named by manufacturer, line and color — and photos of the work when it's done." },
  { icon: "Award", title: "Valley Roofs, Specifically", desc: "Tile over failing felt, foam that needs recoating, shingles cooked by 115° sun — the problems Phoenix roofs actually have." },
  { icon: "Layers", title: "Repair Before Replace", desc: "On most Valley tile roofs the tile outlives the felt beneath it. When new underlayment is the real fix, we say so instead of selling a whole new roof." },
  { icon: "MonitorSmartphone", title: "Your Own Customer Portal", desc: "A private portal on every job, powered by RafterCore — view estimates and invoices, track job progress and photos, read your scope and warranty, see maintenance due, and message us in one thread." },
  { icon: "Clock", title: "On-Time, On-Budget", desc: "Clear timelines, transparent pricing, and no surprises." },
  { icon: "Languages", title: "Hablamos Español", desc: "Your estimate, scope and paperwork explained in Spanish or English — whichever you'd rather do business in." },
];
